import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import { FiChevronDown } from "react-icons/fi";
import { LuFilter } from "react-icons/lu";
import { FilterSection, filterValueLabel } from "./HeaderFilters";
import { useTranslation } from "react-i18next";

const isSet = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

// What a collapsed row shows: the picked option's label, typed text, or Any.
// Several picked: their labels (or the preset's name), as the chips show.
const shownValue = (filter, value) => {
  if (!isSet(value)) return null;
  if (filter.multiple) return filterValueLabel(filter, value);
  const option = filter.options?.find(
    (opt) =>
      String(opt.value).toLowerCase() === String(value).trim().toLowerCase(),
  );
  return option ? option.label : `“${String(value).trim()}”`;
};

// The heading filters of every visible column, grouped by column.
export const collectHeaderFilters = (columns) =>
  (columns ?? [])
    .filter(
      (col) =>
        col?.checked !== false &&
        !col?.collapsed &&
        Array.isArray(col?.headerFilters) &&
        col.headerFilters.length > 0,
    )
    .map((col) => ({
      key: String(col.id ?? col.field_name),
      label: col.label,
      filters: col.headerFilters,
    }));

/**
 * Phones: the column headings sit off-screen, so a "Filters" button opens
 * every column's heading filters (`headerFilters`) in one bottom sheet, as
 * one-line rows showing their current value; tapping a row opens it (one at
 * a time) with the same search, list and counts as the heading pop-up, and
 * it sends the same &field=value. Renders nothing for tables without
 * heading filters.
 */
export default function FiltersSheet({ columns, filterValues, onApply }) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  // The one filter whose row is expanded (others stay one-line rows).
  const [expanded, setExpanded] = useState(null);
  const groups = collectHeaderFilters(columns);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!groups.length) return null;

  const active = groups
    .flatMap((group) => group.filters)
    .filter((filter) => isSet(filterValues?.[filter.field_name])).length;
  const close = () => {
    setOpen(false);
    setExpanded(null);
  };

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className={`inline-flex shrink-0 items-center gap-[6px] rounded-full border-[1px] px-[12px] py-[5px] text-xs font-medium ${
          active
            ? "border-brand bg-brand-light text-brand"
            : "border-ui-border bg-ui-surface text-ui-text-muted"
        }`}
      >
        <LuFilter className="h-[13px] w-[13px] shrink-0" />
        {t("table.filters")}
        {active > 0 && (
          <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-brand px-[5px] text-[10px] font-semibold leading-[16px] text-[#fff]">
            {active}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[58] bg-black/40" onClick={close} />
            <div
              role="dialog"
              aria-label={t("table.filters")}
              className="fixed inset-x-0 bottom-0 z-[59] flex max-h-[85vh] flex-col rounded-t-[14px] border-t-[1px] border-ui-border bg-ui-surface-raised shadow-ui-md"
            >
              <div className="flex items-center justify-between border-b-[1px] border-ui-border px-[14px] py-[10px]">
                <span className="text-sm font-semibold text-ui-text">
                  {t("table.filters")}
                </span>
                <button
                  type="button"
                  aria-label={t("table.closeFilters")}
                  onClick={close}
                  className="flex h-[32px] w-[32px] items-center justify-center rounded-md text-ui-text-subtle hover:text-ui-text"
                >
                  <IoClose className="h-[18px] w-[18px]" />
                </button>
              </div>
              <div className="thin__scrollbar min-h-0 flex-1 overflow-y-auto pb-[8px]">
                {groups.map((group) => (
                  <section
                    key={group.key}
                    aria-label={group.label}
                    className="border-b-[1px] border-ui-border last:border-b-0"
                  >
                    <h3 className="mb-0 px-[12px] pt-[10px] text-[11px] font-semibold uppercase tracking-wide text-ui-text">
                      {group.label}
                    </h3>
                    {group.filters.map((filter) => {
                      const value = filterValues?.[filter.field_name];
                      const current = shownValue(filter, value);
                      const isOpen = expanded === filter.field_name;
                      return (
                        <div key={filter.field_name}>
                          <button
                            type="button"
                            aria-expanded={isOpen}
                            onClick={() =>
                              setExpanded(isOpen ? null : filter.field_name)
                            }
                            className="flex min-h-[44px] w-full items-center gap-[10px] border-0 bg-transparent px-[12px] py-[8px] text-left"
                          >
                            <span className="flex-1 text-sm text-ui-text">
                              {filter.label}
                            </span>
                            <span
                              className={`min-w-0 max-w-[50%] truncate text-sm ${
                                current
                                  ? "font-medium text-brand"
                                  : "text-ui-text-subtle"
                              }`}
                            >
                              {current ?? t("table.any")}
                            </span>
                            <FiChevronDown
                              aria-hidden="true"
                              className={`h-[16px] w-[16px] shrink-0 text-ui-text-subtle transition-transform ${
                                isOpen ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                          {isOpen && (
                            <FilterSection
                              filter={filter}
                              value={value}
                              onChange={onApply}
                              onClose={close}
                              large={true}
                              showLabel={false}
                            />
                          )}
                        </div>
                      );
                    })}
                  </section>
                ))}
              </div>
              <div className="border-t-[1px] border-ui-border px-[14px] py-[10px]">
                <button
                  type="button"
                  onClick={close}
                  className="w-full rounded-md bg-brand py-[10px] text-sm font-semibold text-[#fff]"
                >
                  {t("actions.showResults")}
                </button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
